import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FeedbackChat } from './FeedbackChat';
import { useFeedbackChat } from './useFeedbackChat';

interface Props {
  appCode: string;
  state: 'expanded' | 'minimized';
  onExpand: () => void;
  onMinimize: () => void;
  onClose: () => void;
}

const POS_KEY = 'dev-feedback-panel-pos';

/** 모바일(<768px): 화면 절반 bottom-sheet · 데스크탑: 드래그 가능 float window */
const useIsMobile = (): boolean => {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768,
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

export const FeedbackPanel: React.FC<Props> = ({ appCode, state, onExpand, onMinimize, onClose }) => {
  const chat = useFeedbackChat({ appCode });
  const isMobile = useIsMobile();

  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      return raw ? JSON.parse(raw) : { x: 60, y: 60 };
    } catch {
      return { x: 60, y: 60 };
    }
  });
  const dragging = useRef<{ ox: number; oy: number } | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(pos));
    } catch {
      /* ignore */
    }
  }, [pos]);

  useEffect(() => {
    const mv = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPos({
        x: Math.max(0, e.clientX - dragging.current.ox),
        y: Math.max(0, e.clientY - dragging.current.oy),
      });
    };
    const up = () => {
      dragging.current = null;
    };
    window.addEventListener('mousemove', mv);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', mv);
      window.removeEventListener('mouseup', up);
    };
  }, []);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state === 'expanded') onMinimize();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [state, onMinimize]);

  const onHeaderDown = (e: React.MouseEvent) => {
    dragging.current = { ox: e.clientX - pos.x, oy: e.clientY - pos.y };
  };

  if (typeof document === 'undefined') return null;

  if (state === 'minimized') {
    // 모바일은 런처(🐶) 가 이미 하단 노출 중이므로 별도 pill 없음 — 그냥 hidden
    // (사용자가 다시 🐶 tap 하면 expanded 재진입)
    if (isMobile) return null;
    return createPortal(
      <button
        data-dev-feedback-panel
        onClick={onExpand}
        style={{
          position: 'fixed',
          left: pos.x,
          top: pos.y,
          zIndex: 2147483646,
          background: '#e94d20',
          color: 'white',
          border: 'none',
          borderRadius: 20,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}
      >
        💬 피드백 {chat.messages.length > 0 ? `· draft ${chat.messages.length}` : ''}
      </button>,
      document.body,
    );
  }

  // ---------- 모바일 bottom-sheet (expanded) ----------
  if (isMobile) {
    return createPortal(
      <>
        {/* 배경 dim — 탭하면 최소화 */}
        <div
          onClick={onMinimize}
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483645,
            background: 'rgba(0,0,0,0.35)',
          }}
        />
        <div
          data-dev-feedback-panel
          role="dialog"
          aria-label="개발 피드백"
          style={{
            position: 'fixed',
            left: 0, right: 0, bottom: 0,
            zIndex: 2147483646,
            height: '55vh',
            maxHeight: '80vh',
            background: 'white',
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
            boxShadow: '0 -6px 24px rgba(0,0,0,0.28)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          {/* drag handle bar */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 2px' }}>
            <span style={{ width: 40, height: 4, borderRadius: 2, background: '#d0d0d0' }} />
          </div>
          <div
            style={{
              padding: '4px 12px 8px',
              background: '#20314D',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              💬 피드백 · {appCode} · {typeof window !== 'undefined' ? window.location.pathname : ''}
            </span>
            <button
              title="최소화 (배경 탭 또는 이 버튼)"
              onClick={onMinimize}
              style={{
                background: 'transparent', border: 'none', color: 'white',
                cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 6px',
              }}
            >
              —
            </button>
            <button
              title="닫기 (draft 유지)"
              onClick={onClose}
              style={{
                background: 'transparent', border: 'none', color: 'white',
                cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 6px',
              }}
            >
              ×
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <FeedbackChat chat={chat} appCode={appCode} onSubmitted={() => onMinimize()} onClose={onClose} compact />
          </div>
        </div>
      </>,
      document.body,
    );
  }

  return createPortal(
    <div
      data-dev-feedback-panel
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: 400,
        height: 560,
        zIndex: 2147483646,
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: 8,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        onMouseDown={onHeaderDown}
        style={{
          padding: '8px 12px',
          background: '#20314D',
          color: 'white',
          cursor: 'move',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          userSelect: 'none',
        }}
      >
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>
          💬 피드백 · {appCode} ·{' '}
          {typeof window !== 'undefined' ? window.location.pathname : ''}
        </span>
        <button
          title="최소화"
          onClick={onMinimize}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          —
        </button>
        <button
          title="닫기 (draft 유지)"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <FeedbackChat chat={chat} appCode={appCode} onSubmitted={() => onMinimize()} onClose={onClose} compact />
      </div>
    </div>,
    document.body,
  );
};
